import React, { memo, useEffect, useCallback, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { createSelector } from 'reselect';
import Swiper from 'react-id-swiper';
import 'swiper/css/swiper.css';
import ApkButton from '../../components/Button/Button';
import Button from '../../components/Button/Button';
import { logging, logPostInfo } from '../../actions/log';
import { CONTEXT } from '../../config';
import styles from './SoftBanner.module.less';
import { useInView } from 'react-intersection-observer';
import {
  loggingADX,
  logNative,
  getUrlByTargetContent,
  hasClass,
  removeClass,
  addClass,
} from '../../utils/utils';
import { setIframeVisible } from '../../actions/layout';
import { education } from '../../constants/storageKeys';

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

export default memo(({ data, logInfo }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const logInfoData = useSelector(selectLogInfo);

  const swiperEl = useRef(null);
  const [banners, setBanners] = useState(null);
  const [swiperParams, setSwiperParams] = useState(null);
  const activeBannerKey = useRef(null);

  const maxBannerNum = 15;
  const [ref, inView] = useInView({
    threshold: 1,
  });
  useEffect(() => {
    if (activeBannerKey && activeBannerKey.current) {
      activeBannerKey.current.inView = inView;
    } else {
      activeBannerKey.current = {
        inView,
      };
    }
  }, [inView]);

  const changView = activeKey => {
    if (!activeBannerKey.current || !activeBannerKey.current.activeIndex) {
      return;
    }
    try {
      let { pageCardContentId } =
        data && data.dataList && data.dataList[(activeKey - 1) % data.dataList.length];
      if (
        activeBannerKey.current.inView &&
        logInfo &&
        logInfo.changView &&
        activeKey !== activeBannerKey.current.activeIndex &&
        pageCardContentId
      ) {
        logInfo.changView({
          pageCardId: pageCardContentId,
          pageCardTitle: data && data.title,
          rankCode: data && data.dataList && data.dataList[0] && data.dataList[0].code,
          cardType: data && data.cardType,
        });
      }
    } catch (err) {
      logNative(err);
    }
  };

  const LOG_ACTION_SOURCE = {
    c_action: `${logInfo.tag}_Banner`,
    c_action_btn: `${logInfo.tag}_Banner_btn`,
    c_action_adx: `${logInfo.tag}_Banner_ADX`,
    p_adx: `${logInfo.tag}_Banner_ADX`,
    source: `${logInfo.tag}_Banner`,
  };

  // 筛洗数据，去除运营配置数据错误情况
  useEffect(() => {
    if (data && data.dataList) {
      const showBanners = [];
      let hasADXBanner = null;
      data.dataList.forEach(banner => {
        if (
          (banner.targetType === '1' && banner.appInfo) ||
          (banner.targetType === '2' && (banner.targetUrl || banner.targetContent)) ||
          (banner.targetType === '3' && banner.ztId) ||
          (banner.targetType === '5' && banner.targetContent) ||
          (banner.targetType === '11' && banner.targetContent) ||
          (banner.targetType === '12' && banner.targetContent)
        ) {
          showBanners.push(banner);
        }
        // 是否含有adx数据
        if (banner.targetType === '10' && banner.advertCrid && banner.advertUrl) {
          showBanners.push(banner);
          hasADXBanner = banner;
        }
      });
      setBanners(showBanners.slice(0, maxBannerNum));
      getSwiperParams(showBanners.slice(0, maxBannerNum));
      // 含有adx数据，则打展示的桩
      if (hasADXBanner) {
        loggingADXPV(hasADXBanner);
      }
    }
  }, [data]);

  // 由于swiper组件没有封装 hover切换事件，所以添加事件：鼠标hover切换轮播图。
  useEffect(() => {
    const swiper = swiperEl.current && swiperEl.current.swiper;
    if (banners && swiper) {
      const paginations = document.querySelectorAll('.swiper-pagination-bullet');
      paginations.forEach((pagination, index) => {
        pagination.addEventListener(
          'mouseenter',
          () => {
            swiper.slideTo(index + 1);
          },
          false,
        );
      });
    }
  }, [banners, swiperEl]);

  const loggingADXPV = useCallback(bannerADX => {
    // 教育频道带学段信息
    const eduLog =
      logInfo.tag === 'Edu'
        ? {
            age_period: localStorage.getItem(education.EDUSTAGE),
          }
        : {};
    // 推荐页面ADX banner展示打桩
    dispatch(
      logging({
        category: 'I',
        action: LOG_ACTION_SOURCE.p_adx,
        label: '',
        param2: {
          source: `${LOG_ACTION_SOURCE.source}||${bannerADX.pageCardContentId}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          advertCrid: bannerADX.advertCrid,
          advertUrl: bannerADX.advertUrl,
          ...eduLog,
        },
        param3: bannerADX.bizInfo,
      }),
    );
    if (bannerADX && bannerADX.impTracks) {
      const impTracks = JSON.parse(bannerADX.impTracks);
      loggingADX(impTracks);
    }
  }, []);

  const loggingADXClick = useCallback(bannerADX => {
    // 教育频道带学段信息
    const eduLog =
      logInfo.tag === 'Edu'
        ? {
            age_period: localStorage.getItem(education.EDUSTAGE),
          }
        : {};
    // 推荐页面ADX banner点击打桩
    dispatch(
      logging({
        category: 'C',
        action: LOG_ACTION_SOURCE.c_action_adx,
        label: '',
        param2: {
          source: `${LOG_ACTION_SOURCE.source}||${bannerADX.pageCardContentId}`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          advertCrid: bannerADX.advertCrid,
          advertUrl: bannerADX.advertUrl,
          ...eduLog,
        },
        param3: bannerADX.bizInfo,
      }),
    );
    if (bannerADX && bannerADX.impTracks) {
      const clickTracks = JSON.parse(bannerADX.clickTracks);
      loggingADX(clickTracks);
    }
  }, []);

  const getSwiperParams = bannersArr => {
    const params = {
      centeredSlides: true,
      slidesPerView: '1',
      spaceBetween: 0,
      speed: 800,
      loop: true,
      autoplay: {
        delay: 4000,
        stopOnLastSlide: false,
        disableOnInteraction: false,
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
        renderBullet: (index, className) => {
          return `<div class="${className}">
            <div class="swiper_pagination_round"></div>
          </div>`;
        },
      },
      on: {
        click(e) {
          const targetDom = e.path[0] || {};
          const index = (this.activeIndex - 1 + bannersArr.length) % bannersArr.length;
          const bannerInfo = bannersArr[index] || {};
          if (targetDom.className && targetDom.className.indexOf(styles.banner_img) > -1) {
            handleBanner(bannerInfo);
          } else if (targetDom.className && targetDom.className.indexOf('soft_btn_text') > -1) {
            handleBannerBtn(bannerInfo);
          }
        },
        slideChangeTransitionStart(e) {
          // 当鼠标移走，切换动画开始的时候删除之前banner的动画class
          if (swiperEl && swiperEl.current && swiperEl.current.swiper) {
            const swiperObj = swiperEl.current.swiper;
            if (
              swiperObj &&
              swiperObj.slides &&
              swiperObj.slides.length > 0 &&
              swiperObj.activeIndex
            ) {
              const dom = swiperObj.slides[swiperObj.activeIndex];
              const el = dom.getElementsByClassName(styles.banner_img)[0];
              removeClass(el, styles.image_mouse_in);
            }
            if (swiperObj.activeIndex) {
              changView(swiperObj.activeIndex);
              if (activeBannerKey.current) {
                activeBannerKey.current.activeIndex = swiperObj.activeIndex;
              } else {
                activeBannerKey.current = {
                  activeIndex: swiperObj.activeIndex,
                };
              }
            }
          }
        },
      },
    };
    setSwiperParams(params);
  };

  const handleBannerBtn = useCallback(
    bannerInfo => {
      // 教育频道带学段信息
      const eduLog =
        logInfo.tag === 'Edu'
          ? {
              age_period: localStorage.getItem(education.EDUSTAGE),
            }
          : {};
      // 推荐页面banner btn点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_action_btn,
          label: '',
          param2: {
            source: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            ...eduLog,
          },
          param3: bannerInfo.bizInfo,
        }),
      );
    },
    [dispatch, logInfoData],
  );

  const handleBanner = useCallback(
    bannerInfo => {
      // 教育频道带学段信息
      const eduLog =
        logInfo.tag === 'Edu'
          ? {
              age_period: localStorage.getItem(education.EDUSTAGE),
            }
          : {};
      // 推荐页面banner点击打桩
      dispatch(
        logging({
          category: 'C',
          action: LOG_ACTION_SOURCE.c_action,
          label: '',
          param2: {
            source: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            ...eduLog,
          },
          param3: bannerInfo.bizInfo,
        }),
      );

      setTimeout(() => {
        if (
          bannerInfo.targetType === '1' ||
          bannerInfo.targetType === '3' ||
          bannerInfo.targetType === '11' ||
          bannerInfo.targetType === '12'
        ) {
          dispatch(
            logPostInfo({
              source_p: `${LOG_ACTION_SOURCE.source}||${bannerInfo.pageCardContentId}`,
              source_pp: logInfoData.source_p,
            }),
          );
          let url = `${CONTEXT}/`;
          if (bannerInfo.targetType && bannerInfo.targetContent) {
            url = getUrlByTargetContent(bannerInfo);
          }
          history.push(url);
        } else if (bannerInfo.targetType === '2') {
          if (bannerInfo.targetUrl) {
            window.open(bannerInfo.targetUrl);
          } else if (bannerInfo.targetContent) {
            try {
              const targetContent = JSON.parse(bannerInfo.targetContent) || {};
              window.open(targetContent.targetUrl);
            } catch (error) {}
          }
        } else if (bannerInfo.targetType === '5' && bannerInfo.targetContent) {
          let targetContent = {};
          try {
            targetContent = JSON.parse(bannerInfo.targetContent);
          } catch (error) {
            logNative(`分析商城URL JSON解析错误:${error}`, error);
          }
          if (targetContent && targetContent.url) {
            dispatch(
              setIframeVisible({
                visible: true,
                url: targetContent.url,
                source: LOG_ACTION_SOURCE.source,
              }),
            );
          }
        } else if (
          bannerInfo.targetType === '10' &&
          bannerInfo.advertCrid &&
          bannerInfo.advertUrl
        ) {
          loggingADXClick(bannerInfo);
          window.open(bannerInfo.advertUrl);
        } else {
          history.push(`${CONTEXT}/error`);
        }
      }, 30);
    },
    [dispatch, logInfoData, history],
  );

  const onMouseOver = useCallback(e => {
    const el = e.currentTarget.getElementsByClassName(styles.banner_img)[0];
    addClass(el, styles.image_mouse_in);
  }, []);

  return banners && banners.length > 0 ? (
    <div className={styles.banner} ref={ref} data-item={data.cardType}>
      {history &&
        history.location &&
        history.location.pathname === `${CONTEXT}/education/recommend` && (
          <Swiper {...swiperParams} ref={swiperEl} rebuildOnUpdate>
            {banners.map((item, i) => (
              <div
                key={i}
                onMouseEnter={() => {
                  if (swiperEl.current.swiper) swiperEl.current.swiper.autoplay.stop();
                }}
                onMouseLeave={() => {
                  if (swiperEl.current.swiper) swiperEl.current.swiper.autoplay.start();
                }}
                onMouseOver={e => onMouseOver(e)}
              >
                <div className={styles.banner_item} key={i}>
                  <img
                    className={styles.banner_img}
                    src={item.contentImgBig || item.contentImg}
                    alt=""
                  />
                  {item.targetType === '5' && (
                    <div className={styles.banner_infos}>
                      <div
                        className={styles.banner_shop_btn}
                        onClick={() => {
                          handleBanner(item);
                        }}
                        style={{ border: '10ps solid green' }}
                      >
                        购买
                      </div>
                    </div>
                  )}
                  {item.targetType === '1' && (
                    <div className={styles.banner_infos}>
                      {item.appInfo.androidType === '1' ? (
                        <ApkButton
                          soft={item.appInfo}
                          info={{
                            className: styles.banner_soft_button,
                            className_busing: styles.banner_soft_button_busing,
                            className_open: styles.soft_button_open,
                          }}
                          logInfo={{
                            source: `${LOG_ACTION_SOURCE.source}||${item.pageCardContentId}`,
                          }}
                        />
                      ) : (
                        <Button
                          soft={item.appInfo}
                          info={{
                            className: styles.banner_soft_button,
                            className_busing: styles.banner_soft_button_busing,
                            className_open: styles.soft_button_open,
                          }}
                          logInfo={{
                            source: `${LOG_ACTION_SOURCE.source}||${item.pageCardContentId}`,
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Swiper>
        )}
      {history &&
        history.location &&
        history.location.pathname !== `${CONTEXT}/education/recommend` && (
          <Swiper {...swiperParams} ref={swiperEl} rebuildOnUpdate>
            {banners.map((item, i) => (
              <div
                key={i}
                onMouseEnter={() => {
                  if (swiperEl.current.swiper) swiperEl.current.swiper.autoplay.stop();
                }}
                onMouseLeave={() => {
                  if (swiperEl.current.swiper) swiperEl.current.swiper.autoplay.start();
                }}
                onMouseOver={e => onMouseOver(e)}
              >
                <div className={styles.banner_item} key={i}>
                  <img
                    className={styles.banner_img}
                    src={item.contentImgBig || item.contentImg}
                    alt=""
                  />
                  {item.targetType === '5' && (
                    <div className={styles.banner_infos}>
                      <div
                        className={styles.banner_shop_btn}
                        onClick={() => {
                          handleBanner(item);
                        }}
                      >
                        购买
                      </div>
                    </div>
                  )}
                  {item.targetType === '1' && (
                    <div className={styles.banner_infos}>
                      {item.appInfo.androidType === '1' ? (
                        <ApkButton
                          soft={item.appInfo}
                          info={{
                            className: styles.banner_soft_button,
                            className_busing: styles.banner_soft_button_busing,
                            className_open: styles.soft_button_open,
                          }}
                          logInfo={{
                            source: `${LOG_ACTION_SOURCE.source}||${item.pageCardContentId}`,
                          }}
                        />
                      ) : (
                        <Button
                          soft={item.appInfo}
                          info={{
                            className: styles.banner_soft_button,
                            className_busing: styles.banner_soft_button_busing,
                            className_open: styles.soft_button_open,
                          }}
                          logInfo={{
                            source: `${LOG_ACTION_SOURCE.source}||${item.pageCardContentId}`,
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Swiper>
        )}
    </div>
  ) : (
    ''
  );
});
